'use client';

import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'next/navigation';
import Link from 'next/link';
import { PESTS, Pest } from '@/data/pests';
import { PLANTS } from '@/data/plants';
import {
  getPlantHelpMatches,
  FIXED_SYMPTOMS,
} from '@/lib/plantHelpMatcher';
import type { HelpMatch, Confidence } from '@/lib/plantHelpMatcher';
import { useUserData } from '@/context/UserDataContext';
import {
  formatGeneratedProfileError,
  generatePestProfile,
  searchGeneratedPests,
} from '@/lib/generatedProfiles';
import type { GeneratedPest } from '@/lib/generatedProfiles';
import { BENEFICIAL_INSECTS } from '@/data/beneficialInsects';
import { REMEDIES } from '@/data/remedies';
import { getPestBeneficialIds, getPestRemedyIds } from '@/data/pestRelations';
import { recordEngagement } from '@/lib/engagementNudge';
import PestImage from '@/components/PestImage';
import BeneficialImage from '@/components/BeneficialImage';

// ── Constants ──────────────────────────────────────────────────────────────────

const ALL_PLANTS_SENTINEL = '__all__';

// Sorted unique plant names from the full plant catalog
const ALL_PLANT_NAMES = [...new Set(PLANTS.map(p => p.name))].sort((a, b) =>
  a.localeCompare(b),
);

// ── Filter config ──────────────────────────────────────────────────────────────

type TypeFilter = 'all' | Pest['type'];

const TYPE_FILTERS: { value: TypeFilter; label: string }[] = [
  { value: 'all',           label: 'All'          },
  { value: 'insect',        label: 'Insects'      },
  { value: 'disease',       label: 'Diseases'     },
  { value: 'fungal',        label: 'Fungal'       },
  { value: 'animal',        label: 'Animal'       },
  { value: 'deficiency',    label: 'Deficiencies' },
  { value: 'physiological', label: 'Plant Stress' },
];

const TYPE_BADGE: Record<Pest['type'], string> = {
  insect:        'bg-[#C2703E] text-white',
  disease:       'bg-[#B85C5C] text-white',
  fungal:        'bg-[#7C5CBF] text-white',
  animal:        'bg-[#2D5016] text-white',
  deficiency:    'bg-[#8A8678] text-white',
  physiological: 'bg-[#6B8F6E] text-white',
  other:         'bg-[#8A8678] text-white',
};

const TYPE_LABEL: Record<Pest['type'], string> = {
  insect:        'Insect',
  disease:       'Disease',
  fungal:        'Fungal',
  animal:        'Animal',
  deficiency:    'Deficiency',
  physiological: 'Plant Stress',
  other:         'Other',
};

const SYMPTOM_CHIPS = [
  { tag: 'holes',    label: 'Holes in Leaves'  },
  { tag: 'yellowing', label: 'Yellowing Leaves' },
  { tag: 'powder',   label: 'White Powder'      },
  { tag: 'wilt',     label: 'Wilting Plants'    },
  { tag: 'sticky',   label: 'Sticky Residue'    },
  { tag: 'chewing',  label: 'Chewed Stems'      },
  { tag: 'spots',    label: 'Spots on Leaves'   },
  { tag: 'stunted',  label: 'Stunted Growth'    },
];

const CONFIDENCE_STYLES: Record<Confidence, string> = {
  'likely':         'bg-[#E4F0E8] text-[#2F6E4F]',
  'possible':       'bg-[#FEF3C7] text-[#92400E]',
  'worth-checking': 'bg-cream text-sage',
};

const CONFIDENCE_LABELS: Record<Confidence, string> = {
  'likely':         'Likely',
  'possible':       'Possible',
  'worth-checking': 'Worth checking',
};

// ── Icons ──────────────────────────────────────────────────────────────────────

function SearchIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor"
      strokeWidth="1.5" strokeLinecap="round">
      <circle cx="6.5" cy="6.5" r="4.5" />
      <path d="M10.5 10.5L14 14" />
    </svg>
  );
}

function XIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round">
      <path d="M1.5 1.5L10.5 10.5M10.5 1.5L1.5 10.5" />
    </svg>
  );
}

function BackIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 13 13" fill="none" stroke="currentColor"
      strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M8.5 2L3.5 6.5L8.5 11" />
    </svg>
  );
}

function ChevronIcon({ open }: { open: boolean }) {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
      style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 180ms' }}>
      <path d="M2 4l4 4 4-4" />
    </svg>
  );
}

function LeafShieldIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor"
      strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10 2L3 5.5V10c0 4 3 6.5 7 8 4-1.5 7-4 7-8V5.5L10 2z" />
      <path d="M10 14V10" />
      <path d="M10 10C9 10 6.5 9.5 6.5 7.5c0-1.5 1.5-2 2.5-1C9.5 7 10 9 10 10Z" />
      <path d="M10 10C11 10 13.5 9.5 13.5 7.5c0-1.5-1.5-2-2.5-1C10.5 7 10 9 10 10Z" />
    </svg>
  );
}

function EmptyBugIcon() {
  return (
    <svg width="44" height="44" viewBox="0 0 44 44" fill="none" stroke="currentColor"
      strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <ellipse cx="22" cy="24" rx="9" ry="12" />
      <path d="M22 12a4 4 0 0 1 0-8" />
      <path d="M22 12a4 4 0 0 0 0-8" />
      <path d="M13 18L6 14M13 24H6M13 30L6 34" />
      <path d="M31 18l7-4M31 24h7M31 30l7 4" />
    </svg>
  );
}

// ── Shared pest thumbnail ──────────────────────────────────────────────────────

function PestThumbnail({ src, alt, size }: { src?: string | null; alt: string; size: number }) {
  return (
    <div
      className="flex-shrink-0 rounded-lg overflow-hidden border border-sand bg-cream"
      style={{ width: size, height: size }}>
      {src ? (
        <PestImage src={src} alt={alt} width={size} height={size}
          style={{ objectFit: 'cover', width: '100%', height: '100%' }} />
      ) : (
        <div className="w-full h-full flex items-center justify-center text-sage opacity-40">
          <svg width={size * 0.45} height={size * 0.45} viewBox="0 0 14 14" fill="none"
            stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <ellipse cx="7" cy="8" rx="3" ry="4" />
            <path d="M7 4V2M4 8H1M10 8h3M4 6L2 4M10 6l2-2M4 10L2 12M10 10l2 2" />
          </svg>
        </div>
      )}
    </div>
  );
}

// ── Guided: Action panel (shown when a match is selected) ─────────────────────

function ActionPanel({
  match,
  plant,
  onBack,
}: {
  match: HelpMatch;
  plant: string;
  onBack: () => void;
}) {
  const [showMore, setShowMore] = useState(false);
  const pest = match.pest;
  const issue = match.issue;
  const [checkLine, doLine, waitLine] = match.actionGuidance;
  const matchName = issue?.name ?? pest?.name ?? 'Plant problem';
  const hasMore = issue
    ? Boolean(issue.detailGuidance)
    : Boolean(pest && (pest.organicSolutions.length > 1 || pest.preventionTips.length > 0));
  const askUrl = `/ask?plant=${encodeURIComponent(plant === ALL_PLANTS_SENTINEL ? '' : plant)}&pest=${encodeURIComponent(matchName)}`;

  // Track engagement for soft nudge
  useEffect(() => { recordEngagement(); }, []);

  const relatedBeneficials = pest ? getPestBeneficialIds(pest.id)
    .map(id => BENEFICIAL_INSECTS.find(b => b.id === id))
    .filter((b): b is NonNullable<typeof b> => Boolean(b)) : [];

  const relatedRemedies = (issue ? issue.relatedRemedyIds : pest ? getPestRemedyIds(pest.id) : [])
    .map(id => REMEDIES.find(r => r.id === id))
    .filter((r): r is NonNullable<typeof r> => Boolean(r));

  return (
    <div className="space-y-4">
      {/* Back */}
      <button
        onClick={onBack}
        className="flex items-center gap-1.5 font-body text-[13px] text-sage
                   hover:text-deep-green transition-colors">
        <BackIcon />
        Back to results
      </button>

      {/* Illustration */}
      {pest?.image && (
        <div className="flex flex-col items-center gap-2">
          <div className="w-40 h-40 rounded-xl overflow-hidden border border-sand bg-cream">
            <PestImage src={pest.image} alt={pest.name} width={160} height={160}
              style={{ objectFit: 'cover', width: '100%', height: '100%' }} />
          </div>
          <p className="font-body text-[12px] text-sage text-center">
            Compare what you&rsquo;re seeing to this illustration.
          </p>
        </div>
      )}

      {/* Name + confidence */}
      <div className="flex items-center gap-2 flex-wrap">
        <p className="font-body text-[15px] font-semibold text-dark-text">{matchName}</p>
        {issue && (
          <span className="font-body text-[10px] font-semibold px-2 py-[2px] rounded-full bg-light-sage text-deep-green">
            Plant care
          </span>
        )}
        <span className={`font-body text-[10px] font-semibold px-2 py-[2px] rounded-full
                          ${CONFIDENCE_STYLES[match.confidence]}`}>
          {CONFIDENCE_LABELS[match.confidence]}
        </span>
      </div>

      {/* Core guidance */}
      <div className="bg-light-sage rounded-xl px-4 py-4 space-y-3">
        <p className="font-body text-[13px] text-dark-text leading-relaxed">{checkLine}</p>
        <p className="font-body text-[13px] text-dark-text leading-relaxed">{doLine}</p>
        <p className="font-body text-[13px] text-sage leading-relaxed">{waitLine}</p>
      </div>

      {/* Learn more accordion */}
      {hasMore && (
        <div className="border border-sand rounded-xl overflow-hidden">
          <button
            onClick={() => setShowMore(v => !v)}
            className="w-full flex items-center justify-between px-4 py-3
                       hover:bg-light-sage transition-colors">
            <span className="font-body text-[13px] font-semibold text-dark-text">Learn more</span>
            <ChevronIcon open={showMore} />
          </button>
          {showMore && (
            <div className="px-4 pb-4 space-y-4 border-t border-sand pt-3">
              {issue?.detailGuidance && (
                <div>
                  <p className="font-body text-[12px] font-semibold text-sage mb-2 uppercase tracking-wide">
                    Good to know
                  </p>
                  <p className="font-body text-[13px] text-sage leading-relaxed">
                    {issue.detailGuidance}
                  </p>
                </div>
              )}
              {pest && pest.organicSolutions.length > 1 && (
                <div>
                  <p className="font-body text-[12px] font-semibold text-sage mb-2 uppercase tracking-wide">
                    Other options
                  </p>
                  <ul className="space-y-2">
                    {pest.organicSolutions.slice(1, 4).map((sol, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <span className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-sage mt-1.5" />
                        <div>
                          <span className="font-body text-[13px] font-semibold text-dark-text">
                            {sol.name}:{' '}
                          </span>
                          <span className="font-body text-[13px] text-sage">
                            {sol.description.split('.')[0]}.
                          </span>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {pest && pest.preventionTips.length > 0 && (
                <div>
                  <p className="font-body text-[12px] font-semibold text-sage mb-2 uppercase tracking-wide">
                    Prevention
                  </p>
                  <ul className="space-y-1.5">
                    {pest.preventionTips.slice(0, 4).map((tip, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <span className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-sage mt-1.5" />
                        <span className="font-body text-[13px] text-sage leading-snug">{tip}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Natural Control */}
      {relatedBeneficials.length > 0 && (
        <div className="border border-sand rounded-xl p-4">
          <p className="font-body text-[11px] font-semibold text-sage uppercase tracking-wide mb-2.5">
            Natural Control
          </p>
          <div className="space-y-2">
            {relatedBeneficials.map(insect => (
              <Link
                key={insect.id}
                href={`/learn/beneficial`}
                className="flex items-center gap-2.5 rounded-lg p-1 -mx-1
                           hover:bg-light-sage transition-colors">
                <div className="w-8 h-8 rounded-md overflow-hidden border border-sand bg-white flex-shrink-0">
                  <BeneficialImage id={insect.id} name={insect.name} width={32} height={32}
                    style={{ objectFit: 'contain', width: '100%', height: '100%' }} />
                </div>
                <span className="font-body text-[13px] text-deep-green">
                  {insect.name} →
                </span>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Treatment Options */}
      {relatedRemedies.length > 0 && (
        <div className="border border-sand rounded-xl p-4">
          <p className="font-body text-[11px] font-semibold text-sage uppercase tracking-wide mb-2.5">
            Treatment Options
          </p>
          <div className="space-y-2.5">
            {relatedRemedies.map(remedy => (
              <Link
                key={remedy.id}
                href={`/learn/remedies/${remedy.id}`}
                className="block rounded-lg p-1 -mx-1 hover:bg-light-sage transition-colors">
                <p className="font-body text-[13px] font-semibold text-deep-green">
                  {remedy.name} →
                </p>
                <p className="font-body text-[12px] text-sage line-clamp-1 mt-0.5">
                  {remedy.shortDescription}
                </p>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* CTAs */}
      <div className="flex gap-2 pt-1">
        {pest && (
          <Link
            href={`/learn/pests/${pest.id}`}
            className="flex-1 text-center font-body text-[13px] font-semibold
                       text-deep-green border border-deep-green px-4 py-2.5 rounded-xl
                       hover:bg-light-sage transition-colors">
            Full guide →
          </Link>
        )}
        {issue && relatedRemedies[0] && (
          <Link
            href={`/learn/remedies/${relatedRemedies[0].id}`}
            className="flex-1 text-center font-body text-[13px] font-semibold
                       text-deep-green border border-deep-green px-4 py-2.5 rounded-xl
                       hover:bg-light-sage transition-colors">
            Remedy guide →
          </Link>
        )}
        <Link
          href={askUrl}
          className="flex-1 text-center font-body text-[13px] font-semibold text-white
                     bg-deep-green px-4 py-2.5 rounded-xl hover:opacity-90 transition-opacity">
          Ask Garden
        </Link>
      </div>

      {/* Cross-links to full tool pages */}
      <div className="flex items-center gap-3 pt-1">
        <Link href="/learn/beneficial"
          className="font-body text-[12px] text-sage hover:text-deep-green transition-colors">
          See natural controls →
        </Link>
        <span className="text-sand text-[10px]">·</span>
        <Link href="/learn/remedies"
          className="font-body text-[12px] text-sage hover:text-deep-green transition-colors">
          View remedies →
        </Link>
      </div>
    </div>
  );
}

// ── Guided: Result card ────────────────────────────────────────────────────────

function ResultCard({ match, onClick }: { match: HelpMatch; onClick: () => void }) {
  const pest = match.pest;
  const issue = match.issue;
  const matchName = issue?.name ?? pest?.name ?? 'Plant problem';
  const summary = issue?.shortDescription ?? pest?.symptoms[0] ?? pest?.description ?? '';

  return (
    <button
      onClick={onClick}
      className="w-full text-left flex items-center gap-3 px-3 py-3
                 rounded-xl border border-sand hover:border-deep-green
                 hover:bg-light-sage transition-all">
      {pest ? (
        <PestThumbnail src={pest.image} alt={pest.name} size={44} />
      ) : (
        <div
          className="flex-shrink-0 w-11 h-11 rounded-lg border border-sand bg-light-sage
                     flex items-center justify-center text-deep-green">
          <LeafShieldIcon />
        </div>
      )}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap mb-1">
          <span className="font-body text-[14px] font-semibold text-dark-text">
            {matchName}
          </span>
          {issue && (
            <span className="font-body text-[10px] font-semibold px-2 py-[2px] rounded-full bg-light-sage text-deep-green">
              Plant care
            </span>
          )}
          <span className={`font-body text-[10px] font-semibold px-2 py-[2px] rounded-full
                            ${CONFIDENCE_STYLES[match.confidence]}`}>
            {CONFIDENCE_LABELS[match.confidence]}
          </span>
        </div>
        <p className="font-body text-[12px] text-sage leading-snug line-clamp-2">
          {summary}
        </p>
      </div>
      <span className="flex-shrink-0 text-sage text-[14px]">›</span>
    </button>
  );
}

// ── Guided identification section ─────────────────────────────────────────────

function GuidedIdentifier({ initialPlant }: { initialPlant: string }) {
  const { weatherData } = useUserData();
  const sectionRef = useRef<HTMLDivElement>(null);

  const [selectedPlant, setSelectedPlant] = useState(initialPlant);
  const [selectedSymptomId, setSelectedSymptomId] = useState<string | null>(null);
  const [results, setResults] = useState<HelpMatch[] | null>(null);
  const [selectedMatch, setSelectedMatch] = useState<HelpMatch | null>(null);

  // Scroll into view when arriving with a pre-selected plant
  useEffect(() => {
    if (initialPlant && sectionRef.current) {
      setTimeout(() => {
        sectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  }, [initialPlant]);

  const plantNamesForMatcher: string[] =
    selectedPlant === ALL_PLANTS_SENTINEL
      ? ALL_PLANT_NAMES
      : selectedPlant
        ? [selectedPlant]
        : [];

  function handleIdentify() {
    if (!selectedPlant) return;
    setSelectedMatch(null);
    const { matches } = getPlantHelpMatches({
      plantNames: plantNamesForMatcher,
      symptomId: selectedSymptomId,
      weather: weatherData ?? undefined,
      maxResults: 6,
    });
    setResults(matches);
  }

  function handleReset() {
    setSelectedPlant('');
    setSelectedSymptomId(null);
    setResults(null);
    setSelectedMatch(null);
  }

  const canIdentify = !!selectedPlant;
  const plantDisplayName =
    selectedPlant === ALL_PLANTS_SENTINEL ? 'all plants' : selectedPlant;

  return (
    <div
      ref={sectionRef}
      id="identify"
      className="bg-white rounded-xl border border-sand p-5 space-y-5">

      {/* Section heading */}
      <div>
        <h2 className="font-heading text-[20px] text-dark-text leading-tight">
          Identify what&rsquo;s affecting your plant
        </h2>
        <p className="font-body text-[14px] text-sage mt-1">
          Select a plant and describe what you&rsquo;re seeing to find likely causes.
        </p>
      </div>

      {/* Only show inputs when no results yet */}
      {!results && (
        <div className="space-y-4">

          {/* 1. Plant selector */}
          <div className="space-y-1.5">
            <label className="font-body text-[12px] font-semibold text-sage uppercase tracking-wide">
              Which plant?
            </label>
            <select
              value={selectedPlant}
              onChange={e => {
                setSelectedPlant(e.target.value);
                setSelectedSymptomId(null);
                setResults(null);
                setSelectedMatch(null);
              }}
              className="w-full font-body text-[14px] text-dark-text bg-white border border-sand
                         rounded-xl px-4 py-2.5 focus:outline-none focus:border-sage
                         transition-colors appearance-none cursor-pointer">
              <option value="">Select a plant...</option>
              <option value={ALL_PLANTS_SENTINEL}>Not sure which plant</option>
              <optgroup label="Plants">
                {ALL_PLANT_NAMES.map(name => (
                  <option key={name} value={name}>{name}</option>
                ))}
              </optgroup>
            </select>
          </div>

          {/* 2. Symptom selector */}
          <div className="space-y-1.5">
            <label className="font-body text-[12px] font-semibold text-sage uppercase tracking-wide">
              What are you seeing? <span className="font-normal normal-case">(optional)</span>
            </label>
            <div className="grid grid-cols-2 gap-2">
              {FIXED_SYMPTOMS.map(({ id, label }) => (
                <button
                  key={id}
                  onClick={() => setSelectedSymptomId(prev => prev === id ? null : id)}
                  className={`text-left px-4 py-3 rounded-xl border transition-all
                              font-body text-[13px] font-medium leading-snug
                              ${id === 'not-sure'
                                ? `col-span-2 border-dashed ${
                                    selectedSymptomId === id
                                      ? 'border-deep-green bg-light-sage text-deep-green'
                                      : 'border-sand text-sage hover:border-deep-green hover:bg-light-sage'
                                  }`
                                : selectedSymptomId === id
                                  ? 'border-deep-green bg-light-sage text-deep-green'
                                  : 'border-sand text-dark-text hover:border-deep-green hover:bg-light-sage'
                              }`}>
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* 3. Identify button */}
          <button
            onClick={handleIdentify}
            disabled={!canIdentify}
            className="w-full font-body text-[14px] font-semibold text-white bg-deep-green
                       px-4 py-3 rounded-xl hover:opacity-90 transition-opacity
                       disabled:opacity-40 disabled:cursor-not-allowed">
            Identify
          </button>
        </div>
      )}

      {/* Results */}
      {results !== null && !selectedMatch && (
        <div className="space-y-4">
          {/* Summary + reset */}
          <div className="flex items-center justify-between gap-2">
            <p className="font-body text-[13px] text-sage">
              {results.length === 0
                ? `Nothing found for ${plantDisplayName}`
                : results.length === 1
                  ? `1 result for ${plantDisplayName}`
                  : `${results.length} results for ${plantDisplayName}`}
            </p>
            <button
              onClick={handleReset}
              className="font-body text-[13px] text-sage hover:text-deep-green transition-colors">
              Start over
            </button>
          </div>

          {results.length === 0 ? (
            <div className="space-y-3">
              <p className="font-body text-[13px] text-sage leading-relaxed">
                Nothing in the library matches those symptoms. Try browsing the library below, or ask Garden directly.
              </p>
              <div className="flex gap-2">
                <button
                  onClick={handleReset}
                  className="font-body text-[13px] font-semibold text-sage border border-sand
                             px-4 py-2.5 rounded-xl hover:border-sage transition-colors">
                  Try again
                </button>
                <Link
                  href={`/ask?plant=${encodeURIComponent(selectedPlant === ALL_PLANTS_SENTINEL ? '' : selectedPlant)}`}
                  className="font-body text-[13px] font-semibold text-white bg-deep-green
                             px-4 py-2.5 rounded-xl hover:opacity-90 transition-opacity">
                  Ask Garden →
                </Link>
              </div>
            </div>
          ) : (
            <>
              <ul className="space-y-2">
                {results.map((match, index) => (
                  <li key={match.issue?.id ?? match.pest?.id ?? `${match.kind}-${index}`}>
                    <ResultCard match={match} onClick={() => setSelectedMatch(match)} />
                  </li>
                ))}
              </ul>
              <p className="font-body text-[12px] text-sage">
                Not seeing what you expected?{' '}
                <Link
                  href={`/ask?plant=${encodeURIComponent(selectedPlant === ALL_PLANTS_SENTINEL ? '' : selectedPlant)}`}
                  className="text-deep-green hover:underline">
                  Ask Garden →
                </Link>
              </p>
            </>
          )}
        </div>
      )}

      {/* Action panel */}
      {selectedMatch && (
        <ActionPanel
          match={selectedMatch}
          plant={selectedPlant}
          onBack={() => setSelectedMatch(null)}
        />
      )}

    </div>
  );
}

// ── Browse section: pest cards ─────────────────────────────────────────────────

function PestCardImage({ src, alt }: { src?: string | null; alt: string }) {
  if (!src) {
    return (
      <div className="w-20 h-20 flex-shrink-0 rounded-lg overflow-hidden border border-sand bg-cream flex items-center justify-center text-sage">
        <LeafShieldIcon />
      </div>
    );
  }
  return (
    <div className="w-20 h-20 flex-shrink-0 rounded-lg overflow-hidden border border-sand bg-white">
      <PestImage src={src} alt={alt} width={80} height={80}
        style={{ objectFit: 'contain', width: '100%', height: '100%' }} />
    </div>
  );
}

function PestCard({ pest }: { pest: Pest }) {
  const visiblePlants = pest.affectedPlants.slice(0, 4);
  const extraCount    = pest.affectedPlants.length - 4;
  return (
    <Link href={`/learn/pests/${pest.id}`}
      className="group bg-white rounded-xl border border-sand p-5 flex flex-col gap-3
                 hover:border-sage hover:shadow-sm transition-all duration-200">
      <div className="flex items-start justify-between gap-3">
        <span className={`inline-block font-body text-[11px] font-semibold px-2.5 py-0.5 rounded-full
                         ${TYPE_BADGE[pest.type]}`}>
          {TYPE_LABEL[pest.type]}
        </span>
        <PestCardImage src={pest.image} alt={pest.name} />
      </div>
      <div>
        <h3 className="font-heading text-[18px] text-dark-text leading-snug group-hover:text-deep-green
                       transition-colors duration-200">
          {pest.name}
        </h3>
        <p className="font-body text-[14px] text-sage mt-1 leading-relaxed line-clamp-2">
          {pest.description}
        </p>
      </div>
      {pest.affectedPlants.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {visiblePlants.map(plant => (
            <span key={plant}
              className="font-body text-[12px] text-warm-brown bg-cream px-2 py-0.5 rounded-full">
              {plant}
            </span>
          ))}
          {extraCount > 0 && (
            <span className="font-body text-[12px] text-sage px-2 py-0.5">
              +{extraCount} more
            </span>
          )}
        </div>
      )}
      <div className="mt-auto pt-1">
        <span className="font-body text-[13px] font-semibold text-deep-green group-hover:underline">
          View Solutions →
        </span>
      </div>
    </Link>
  );
}

function CommunityPestCard({ pest }: { pest: GeneratedPest }) {
  const visiblePlants = pest.affectedPlants.slice(0, 4);
  const extraCount    = pest.affectedPlants.length - 4;
  const badgeCls      = TYPE_BADGE[pest.type] ?? TYPE_BADGE.other;
  const label         = TYPE_LABEL[pest.type] ?? pest.type;
  return (
    <Link href={`/learn/pests/generated/${pest.id}`}
      className="group bg-white rounded-xl border border-sand p-5 flex flex-col gap-3
                 hover:border-sage hover:shadow-sm transition-all duration-200">
      <div className="flex items-start justify-between gap-3">
        <span className={`inline-block font-body text-[11px] font-semibold px-2.5 py-0.5 rounded-full ${badgeCls}`}>
          {label}
        </span>
        <span className="text-[11px] font-body font-semibold text-sage bg-light-sage px-2 py-[3px] rounded-full">
          Community
        </span>
      </div>
      <div>
        <h3 className="font-heading text-[18px] text-dark-text leading-snug group-hover:text-deep-green transition-colors duration-200">
          {pest.name}
        </h3>
        <p className="font-body text-[14px] text-sage mt-1 leading-relaxed line-clamp-2">
          {pest.description}
        </p>
      </div>
      {pest.affectedPlants.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {visiblePlants.map(plant => (
            <span key={plant}
              className="font-body text-[12px] text-warm-brown bg-cream px-2 py-0.5 rounded-full">
              {plant}
            </span>
          ))}
          {extraCount > 0 && (
            <span className="font-body text-[12px] text-sage px-2 py-0.5">
              +{extraCount} more
            </span>
          )}
        </div>
      )}
      <div className="mt-auto pt-1">
        <span className="font-body text-[13px] font-semibold text-deep-green group-hover:underline">
          View Solutions →
        </span>
      </div>
    </Link>
  );
}

function isMeaningfulPestQuery(query: string): boolean {
  const trimmed = query.trim();
  const letters = trimmed.match(/[a-z]/gi)?.length ?? 0;
  return trimmed.length >= 3 && letters >= 3;
}

// ── Page ───────────────────────────────────────────────────────────────────────

export default function PestsContent() {
  const searchParams = useSearchParams();
  const initialPlant = searchParams.get('plant') ?? '';

  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('all');
  const [activeSymptoms, setActiveSymptoms] = useState<Set<string>>(new Set());
  const [communityPests, setCommunityPests] = useState<GeneratedPest[]>([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationMessage, setGenerationMessage] = useState('');
  const [generationError, setGenerationError] = useState('');

  useEffect(() => {
    searchGeneratedPests('').then(setCommunityPests);
  }, []);

  function toggleSymptom(tag: string) {
    setActiveSymptoms(prev => {
      const next = new Set(prev);
      if (next.has(tag)) next.delete(tag);
      else next.add(tag);
      return next;
    });
  }

  const filtered = PESTS.filter(pest => {
    if (typeFilter !== 'all' && pest.type !== typeFilter) return false;
    if (search.trim() && !pest.name.toLowerCase().includes(search.trim().toLowerCase())) return false;
    if (activeSymptoms.size > 0 && !pest.tags.some(t => activeSymptoms.has(t))) return false;
    return true;
  });

  const filteredCommunityPests = communityPests.filter(pest => {
    if (typeFilter !== 'all' && pest.type !== typeFilter) return false;
    if (search.trim() && !pest.name.toLowerCase().includes(search.trim().toLowerCase())) return false;
    if (activeSymptoms.size > 0 && !(pest.tags ?? []).some(t => activeSymptoms.has(t))) return false;
    return true;
  });

  const totalResults = filtered.length + filteredCommunityPests.length;
  const trimmedSearch = search.trim();
  const canGenerateMissing = totalResults === 0 && isMeaningfulPestQuery(trimmedSearch);

  async function handleGenerateMissingPest() {
    if (!canGenerateMissing || isGenerating) return;
    setIsGenerating(true);
    setGenerationMessage('');
    setGenerationError('');
    try {
      const profile = await generatePestProfile(trimmedSearch);
      if (profile.status === 'approved') {
        setCommunityPests(current => [profile, ...current.filter(item => item.id !== profile.id)]);
        setGenerationMessage(`${profile.name} is available in the community pest library.`);
      } else {
        setGenerationMessage(`${profile.name} draft profile created for admin review.`);
      }
    } catch (error) {
      setGenerationError(formatGeneratedProfileError(
        error,
        'Generation failed. Try a more specific plant pest or disease name.',
      ));
    } finally {
      setIsGenerating(false);
    }
  }

  return (
    <div className="space-y-5">

      {/* Header */}
      <div>
        <div className="flex items-center gap-2 mb-1">
          <Link href="/learn"
            className="font-body text-[13px] text-sage hover:text-deep-green transition-colors">
            ← Learn
          </Link>
        </div>
        <h1 className="font-heading text-[28px] text-dark-text leading-tight">
          Pest &amp; Disease Identifier
        </h1>
        <p className="font-body text-[16px] text-sage mt-1">
          Identify common garden problems and find organic solutions
        </p>
      </div>

      {/* ── Guided identification ─────────────────────────────────────────────── */}
      <GuidedIdentifier initialPlant={initialPlant} />

      {/* Organic pledge callout */}
      <div className="bg-light-sage rounded-xl border border-sage/30 p-4 flex gap-3 items-start">
        <span className="text-deep-green flex-shrink-0 mt-0.5">
          <LeafShieldIcon />
        </span>
        <div>
          <p className="font-body text-[14px] font-semibold text-deep-green">
            100% Organic Solutions Only
          </p>
          <p className="font-body text-[13px] text-sage mt-0.5 leading-relaxed">
            Every solution on this page uses organic methods - no synthetic pesticides,
            no harmful chemicals. Safe for your soil, your plants, and the wildlife that
            visits your garden.
          </p>
        </div>
      </div>

      {/* Photo diagnosis banner */}
      <div className="bg-white rounded-xl border border-sand p-4 flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <span className="text-sage flex-shrink-0">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor"
              strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M1.5 7.5C1.5 6.67 2.17 6 3 6h2.5L7 4h6l1.5 2H17c.83 0 1.5.67 1.5 1.5v9c0 .83-.67 1.5-1.5 1.5H3c-.83 0-1.5-.67-1.5-1.5v-9Z" />
              <circle cx="10" cy="12" r="3" />
            </svg>
          </span>
          <p className="font-body text-[14px] text-dark-text">
            Not sure what you&apos;re dealing with?{' '}
            <span className="text-sage">Upload a photo and let Garden identify it for you.</span>
          </p>
        </div>
        <Link href="/ask"
          className="flex-shrink-0 inline-flex items-center gap-1.5 font-body text-[13px] font-semibold
                     text-deep-green border border-deep-green px-3 py-1.5 rounded-lg
                     hover:bg-light-sage transition-colors">
          Upload a Photo
        </Link>
      </div>

      {/* ── Browse library ────────────────────────────────────────────────────── */}
      <div>
        <h2 className="font-heading text-[20px] text-dark-text mb-4">Browse the library</h2>

        {/* Symptom quick-filter chips */}
        <div className="mb-4">
          <p className="font-body text-[12px] font-semibold text-sage uppercase tracking-wide mb-2">
            Filter by symptom
          </p>
          <div className="overflow-x-auto -mx-4 px-4">
            <div className="flex gap-2 pb-1" style={{ width: 'max-content' }}>
              {SYMPTOM_CHIPS.map(({ tag, label }) => {
                const active = activeSymptoms.has(tag);
                return (
                  <button key={tag} onClick={() => toggleSymptom(tag)}
                    className={`font-body text-[13px] font-medium px-3.5 py-1.5 rounded-full border
                                whitespace-nowrap transition-all duration-150
                                ${active
                                  ? 'bg-deep-green text-white border-deep-green'
                                  : 'bg-white text-dark-text border-sand hover:border-sage'
                                }`}>
                    {label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Search + type filters */}
        <div className="space-y-3 mb-4">
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sage pointer-events-none">
              <SearchIcon />
            </span>
            <input type="text" value={search}
              onChange={e => { setSearch(e.target.value); setGenerationMessage(''); setGenerationError(''); }}
              placeholder="Search pests and diseases..."
              className="w-full font-body text-[15px] text-dark-text bg-white border border-sand
                         rounded-xl pl-9 pr-9 py-2.5 focus:outline-none focus:border-sage
                         placeholder:text-sand transition-colors"
            />
            {search && (
              <button
                onClick={() => { setSearch(''); setGenerationMessage(''); setGenerationError(''); }}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-sand hover:text-sage transition-colors">
                <XIcon />
              </button>
            )}
          </div>
          <div className="flex gap-2 flex-wrap">
            {TYPE_FILTERS.map(({ value, label }) => (
              <button key={value} onClick={() => setTypeFilter(value)}
                className={`font-body text-[13px] font-medium px-4 py-1.5 rounded-full border
                            transition-all duration-150
                            ${typeFilter === value
                              ? 'bg-deep-green text-white border-deep-green'
                              : 'bg-white text-dark-text border-sand hover:border-sage'
                            }`}>
                {label}
              </button>
            ))}
          </div>
        </div>

        {(generationMessage || generationError) && (
          <div className={`rounded-xl border p-4 font-body text-[14px] mb-4 ${
            generationError
              ? 'border-alert-red/30 bg-white text-alert-red'
              : 'border-sage/30 bg-light-sage text-deep-green'
          }`}>
            {generationError || generationMessage}
          </div>
        )}

        {/* Results count + clear */}
        <div className="flex items-center justify-between mb-4">
          <p className="font-body text-[13px] text-sage">
            {totalResults === PESTS.length + communityPests.length
              ? `${PESTS.length} pests & diseases`
              : `${totalResults} of ${PESTS.length + communityPests.length} results`}
          </p>
          {(activeSymptoms.size > 0 || search || typeFilter !== 'all') && (
            <button
              onClick={() => { setSearch(''); setTypeFilter('all'); setActiveSymptoms(new Set()); }}
              className="font-body text-[13px] text-sage hover:text-deep-green transition-colors">
              Clear all filters
            </button>
          )}
        </div>

        {/* Pest grid */}
        {totalResults > 0 ? (
          <>
            {filtered.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {filtered.map(pest => <PestCard key={pest.id} pest={pest} />)}
              </div>
            )}
            {filteredCommunityPests.length > 0 && (
              <div className="space-y-4 pt-6 border-t border-sand">
                <div>
                  <h2 className="font-heading text-[22px] text-dark-text">Community Pests</h2>
                  <p className="font-body text-[14px] text-sage mt-0.5">
                    Reviewed profiles for pests and diseases outside our core library
                  </p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {filteredCommunityPests.map(pest => <CommunityPestCard key={pest.id} pest={pest} />)}
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="bg-white rounded-xl border border-sand p-10 flex flex-col
                          items-center justify-center text-center gap-3">
            <span className="text-sand"><EmptyBugIcon /></span>
            <div>
              <p className="font-heading text-[18px] text-dark-text">No results found</p>
              <p className="font-body text-[14px] text-sage mt-1">
                Try adjusting your search or create a draft profile for review
              </p>
            </div>
            {canGenerateMissing && (
              <div className="max-w-md space-y-3">
                <p className="font-body text-[13px] text-sage leading-relaxed">
                  Garden can generate a plant pest or disease draft automatically. It will go to admin review before being published.
                </p>
                <button onClick={handleGenerateMissingPest} disabled={isGenerating}
                  className="font-body text-[13px] font-semibold text-white bg-deep-green px-4 py-2.5 rounded-lg
                             hover:opacity-90 transition-opacity disabled:opacity-60">
                  {isGenerating ? 'Creating draft...' : `Create draft for '${trimmedSearch}'`}
                </button>
              </div>
            )}
            <button
              onClick={() => { setSearch(''); setTypeFilter('all'); setActiveSymptoms(new Set()); setGenerationMessage(''); setGenerationError(''); }}
              className="mt-1 font-body text-[13px] font-semibold text-deep-green hover:underline transition-colors">
              Clear all filters
            </button>
          </div>
        )}
      </div>

    </div>
  );
}
